import React from "react";
import { twMerge } from "tailwind-merge";
import { ChartLabelledBy } from "../charts/ChartHeader/ChartHeader";
import { Heading } from "../Heading";
import { Icon } from "../Icon";

/**
 * Section Component — Semantic content grouping for dashboards
 *
 * A lightweight container that provides structure and hierarchy to page content.
 * Less heavy than a Card, more structured than a div. Perfect for organizing
 * related content with consistent spacing and optional headers.
 *
 * Usage:
 * ```tsx
 * <Section
 *   title="Overview"
 *   description="Key metrics and performance indicators"
 *   actions={<Button variant="ghost" size="sm">Refresh</Button>}
 * >
 *   <Grid cols={{ base: 1, lg: 3 }}>
 *     <Card>...</Card>
 *   </Grid>
 * </Section>
 * ```
 */

// ——————————————————————————————————————————
// Types
// ——————————————————————————————————————————

/**
 * Dispatched on `window` by `SectionNav` before it scrolls to a section, with the target element's DOM id as
 * `detail.id`. A collapsed `Section` with that id opens itself, so the rail never scrolls to a closed body.
 */
export const SECTION_NAVIGATE_EVENT = "edges:section-navigate";

export type SectionVariant = "plain" | "contained" | "bordered";
export type SectionSpacing = "none" | "sm" | "md" | "lg";

export interface SectionProps {
  /** Section ID for linking/navigation (e.g., for use with SectionNav) */
  id?: string;
  /** Section title */
  title?: React.ReactNode;
  /** Optional description/subtitle */
  description?: React.ReactNode;
  /** Actions slot (buttons, links, etc.) */
  actions?: React.ReactNode;
  /** Visual variant */
  variant?: SectionVariant;
  /** Vertical spacing around section */
  spacing?: SectionSpacing;
  /** Show divider under header */
  withDivider?: boolean;
  /** Heading level for accessibility */
  headingAs?: "h2" | "h3" | "h4";
  /** Additional CSS classes */
  className?: string;
  /** Section content */
  children?: React.ReactNode;
  /** Label used by in-page navigation (PageLayout.Content with withSectionNav). Falls back to title when omitted */
  navLabel?: string;
  /** Lets the reader fold the body away; the title becomes the toggle */
  collapsible?: boolean;
  /** Initial state of a collapsible section. A collapsed body is not mounted */
  defaultCollapsed?: boolean;
  /** Short trailing marker beside a collapsible title, e.g. a count */
  badge?: React.ReactNode;
}

// ——————————————————————————————————————————
// Styles
// ——————————————————————————————————————————

const variantStyles: Record<SectionVariant, string> = {
  plain: "",
  contained: "bg-background-surface rounded-lg border border-border-default p-6",
  bordered: "border-y border-border-default py-6",
};

const spacingStyles: Record<SectionSpacing, string> = {
  none: "",
  sm: "my-4",
  md: "my-6 md:my-8",
  lg: "my-8 md:my-12",
};

// ——————————————————————————————————————————
// Component
// ——————————————————————————————————————————

export function Section({
  id,
  title,
  description,
  actions,
  variant = "plain",
  spacing = "none",
  withDivider = false,
  headingAs = "h2",
  className,
  children,
  collapsible = false,
  defaultCollapsed = false,
  badge,
}: SectionProps) {
  // Generate unique ID for accessibility
  const generatedHeadingId = React.useId();
  const headingId = id ? `${id}-heading` : generatedHeadingId;
  const bodyId = `${headingId}-body`;
  const hasHeader = title || description || actions;
  const [collapsed, setCollapsed] = React.useState(collapsible && defaultCollapsed);
  // Until the reader or a navigation opens or closes it, the section follows `defaultCollapsed` as it changes.
  const settled = React.useRef(false);
  const isOpen = !collapsible || !collapsed;

  React.useEffect(() => {
    if (!settled.current) setCollapsed(collapsible && defaultCollapsed);
  }, [collapsible, defaultCollapsed]);

  React.useEffect(() => {
    if (!collapsible || !id) return;
    const open = () => {
      settled.current = true;
      setCollapsed(false);
    };
    const openIfTargeted = () => {
      if (window.location.hash === `#${id}`) open();
    };
    const onNavigate = (event: Event) => {
      if ((event as CustomEvent<{ id?: string }>).detail?.id === id) open();
    };
    openIfTargeted();
    window.addEventListener(SECTION_NAVIGATE_EVENT, onNavigate);
    // `popstate`, not only `hashchange`: SectionNav writes the hash with pushState, so back/forward fires popstate.
    window.addEventListener("hashchange", openIfTargeted);
    window.addEventListener("popstate", openIfTargeted);
    return () => {
      window.removeEventListener(SECTION_NAVIGATE_EVENT, onNavigate);
      window.removeEventListener("hashchange", openIfTargeted);
      window.removeEventListener("popstate", openIfTargeted);
    };
  }, [collapsible, id]);

  const heading = !title ? null : collapsible ? (
    <Heading tag={headingAs} size="md" className="text-text-primary">
      <button
        type="button"
        onClick={() => {
          settled.current = true;
          setCollapsed((value) => !value);
        }}
        aria-expanded={isOpen}
        aria-controls={bodyId}
        className="flex w-full items-center gap-2 text-left"
      >
        <Icon
          name="CaretRight"
          size="sm"
          aria-hidden
          className={twMerge("text-text-secondary transition-transform", isOpen && "rotate-90")}
        />
        <span>{title}</span>
        {badge != null && <span className="text-sm font-normal text-text-secondary">{badge}</span>}
      </button>
    </Heading>
  ) : (
    <Heading tag={headingAs} size="md" className="text-text-primary">
      {title}
    </Heading>
  );

  return (
    <section
      id={id}
      className={twMerge(spacingStyles[spacing], variantStyles[variant], className)}
      aria-labelledby={title ? headingId : undefined}
    >
      {hasHeader && (
        <header
          id={headingId}
          className={twMerge(
            // `flex-wrap`: the actions drop BELOW the title when the header cannot
            // fit both, instead of the two overlapping. A wide action cluster (the
            // State history metric/category/range controls) needs far more room
            // than `sm` assumes, and the old rule only stacked below that
            // breakpoint — so at 800px the controls ran straight through the
            // heading.
            "flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:justify-between gap-4",
            withDivider && "border-b border-border-default pb-4 mb-6",
            !withDivider && children && isOpen && "mb-6"
          )}
        >
          <div className="min-w-0 flex-1">
            {heading}
            {description && <p className="mt-2 text-base text-text-secondary">{description}</p>}
          </div>
          {/*
            `min-w-0 shrink` rather than `flex-shrink-0`: the slot takes the width
            its contents want, and yields when the header cannot fit them beside
            the title. `flex-shrink-0` collapsed a wide action cluster (the State
            history metric/category/range controls) to its narrowest column —
            ~250px in a 1018px header — so it stacked vertically however much room
            the page had; refusing to shrink at all pushed it past the section
            edge instead. The header already wraps to a column below `sm`.
          */}
          {actions && <div className="min-w-0 shrink">{actions}</div>}
        </header>
      )}

      {children && isOpen && (
        <div id={bodyId} className={variant === "contained" ? "" : ""}>
          {/* A titled Section already labels the charts inside it — its heading is
              the one a reader sees, so they must not add a second. An untitled
              Section stays transparent rather than granting the title: nested
              inside a titled Section it would otherwise re-title every chart
              below it. */}
          {title ? <ChartLabelledBy>{children}</ChartLabelledBy> : children}
        </div>
      )}
    </section>
  );
}

Section.displayName = "Section";
